import { useEffect, useRef, useState } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import getUserMenuItems from "./AdminMenuContent";
import AdminSideBar from "./AdminSideBar";
import {
  superAdminReqConfig,
  api,
  baseURL,
  adminReqConfig,
} from "../../include/api";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminFooter from "../common/AdminFooter";
import "../../styles/CategoryProductList.css";
import { useNotification } from "../../Toast/ToastProvider";
import { ChevronDown, X } from "react-feather";
import { ERROR, SUCCESS } from "../../include/constant";
import { getAdmin } from "../../include/function";

const ListSlide = () => {
  const navigate = useNavigate();
  const homeLink = "/admin";
  const [fetchLoading, setFetchLoading] = useState(true);
  const [slideList, setSlideList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(initialQuery);

  useEffect(() => {
    const q = searchParams.get("q") || "";
    setSearchQuery(q);
  }, [searchParams]);
  const [filterValues, setFilterValues] = useState({
    slideType: "",
    view: "",
    status: "",
  });
  const user = getAdmin();
  const [productPermissions, setProductPermissions] = useState({
    add: false,
    view: false,
    edit: false,
    delete: false,
  });
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.roleId) return;

      try {
        const res = await api.get(
          `/role_permission/?roleId=${user.roleId}`,
          adminReqConfig()
        );
        if (res.status === 200 && Array.isArray(res.data.data)) {
          // Find permission entry where pageTitle === "Product"
          const productPerm = res.data.data.find(
            (p) => p.pageTitle === "Content"
          );

          if (productPerm) {
            setProductPermissions({
              add: productPerm.add === "true" || productPerm.add === 1,
              view: productPerm.view === "true" || productPerm.view === 1,
              edit: productPerm.edit === "true" || productPerm.edit === 1,
              delete: productPerm.delete === "true" || productPerm.delete === 1,
            });
          }
        }
      } catch (err) {
        console.warn("Failed to fetch product permissions", err);
      }
    };

    fetchPermissions();
  }, [user?.roleId]);
  const filterWrapperRef = useRef(null);

  let currentItems = [];
  let totalPage = 1;
  let showingFrom = 0;
  let showingTo = 0;
  if (itemsPerPage === "all") {
    currentItems = filteredData;
    showingFrom = filteredData.length > 0 ? 1 : 0;
    showingTo = filteredData.length;
  } else {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    totalPage = Math.ceil(filteredData.length / itemsPerPage);
    showingFrom = indexOfFirstItem + 1;
    showingTo = Math.min(indexOfLastItem, filteredData.length);
  }

  const toast = useNotification();

  const getSlides = () => {
    setFetchLoading(true);
    api
      .get("/slide_show/", superAdminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          const slides = response.data.data || [];
          setSlideList(slides);
          setFilteredData(slides);
        }
      })
      .catch(() => {
        setSlideList([]);
        setFilteredData([]);
      })
      .finally(() => setFetchLoading(false));
  };

  useEffect(() => {
    getSlides();
  }, []);

  // Apply filters when any filter or search changes
  useEffect(() => {
    let filtered = [...slideList];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (slide) =>
          (slide.name && slide.name.toLowerCase().startsWith(query)) ||
          (slide.title && slide.title.toLowerCase().startsWith(query))
      );
    }

    // Slide Type filter
    if (filterValues.slideType) {
      filtered = filtered.filter(
        (slide) => slide.type === filterValues.slideType
      );
    }

    // View filter
    if (filterValues.view) {
      filtered = filtered.filter((slide) => slide.view === filterValues.view);
    }

    // Status filter
    if (filterValues.status) {
      filtered = filtered.filter(
        (slide) => slide.status === filterValues.status
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchQuery, filterValues, slideList, searchParams]);

  // Close filter panel on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isFilterPanelOpen &&
        filterWrapperRef.current &&
        !filterWrapperRef.current.contains(event.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isFilterPanelOpen]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleView = (slideId) => {
    navigate(`/admin/content/contentAddNew?id=${slideId}&view=true`);
  };

  const handleEdit = (slideId) => {
    navigate(`/admin/content/contentAddNew?id=${slideId}`);
  };

  const handleDelete = (slideId) => {
    if (window.confirm("Are you sure you want to delete this slide?")) {
      api
        .delete(`/slide_show/?id=${slideId}`, adminReqConfig())
        .then((response) => {
          if (response.status === 200) {
            toast({
              type: SUCCESS,
              message: response.data.message || "Slide deleted successfully!",
            });
            setSlideList((prev) => prev.filter((s) => s.id !== slideId));
            setFilteredData((prev) => prev.filter((s) => s.id !== slideId));
          }
        })
        .catch(() => {
          toast({ type: ERROR, message: "Failed to delete slide." });
        });
    }
  };

  const handleStatusChange = (slideId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const payload = { status: newStatus };
    api
      .put(
        `/slide_show/?id=${slideId}&flag=changeStatus`,
        payload,
        adminReqConfig()
      )
      .then((response) => {
        if (response.status === 200) {
          toast({
            type: SUCCESS,
            message: `Slide ${newStatus} successfully!`,
          });
          setSlideList((prev) =>
            prev.map((p) =>
              p.id === slideId ? { ...p, status: newStatus } : p
            )
          );
          setFilteredData((prev) =>
            prev.map((p) =>
              p.id === slideId ? { ...p, status: newStatus } : p
            )
          );
        }
      })
      .catch((error) => {
        console.error("Status update failed:", error);
        toast({ type: ERROR, message: "Failed to update slide status." });
      });
  };

  const getViewLabel = (view) => {
    if (view === "mobile") return "Mobile App";
    if (view === "web") return "Website";
    return view || "-";
  };

  const applyFilters = () => {
    setIsFilterPanelOpen(false);
  };

  const clearFilters = () => {
    setFilterValues({ slideType: "", view: "", status: "" });
    setIsFilterPanelOpen(false);
  };

  // Extract unique values for filters
  const uniqueSlideTypes = [
    ...new Set(slideList.map((s) => s.type).filter(Boolean)),
  ];
  const uniqueViews = [
    ...new Set(slideList.map((s) => s.view).filter(Boolean)),
  ];

  return (
    <>
      <AdminSideBar
        menuContent={menuContent}
        isMinimized={isSidebarMinimized}
      />
      <AdminTopNavBar
        toggleSidebar={toggleSidebar}
        isSidebarMinimized={isSidebarMinimized}
        homeLink={homeLink}
        title="SLIDES"
      />
      <div
        className="content-wrapper p-0"
        style={{ marginLeft: isSidebarMinimized ? "80px" : "250px", flex: "1" }}
      >
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
          }}
        >
          {fetchLoading ? (
            <p>Loading slides...</p>
          ) : (
            <>
              <div className="row pt-32 ps-16 pe-16 align-items-center">
                <div className="col-auto">
                  <h6 className="mb-0">All Slides</h6>
                </div>
                <div className="col d-flex align-items-center">
                  <div
                    className="filter-wrapper"
                    style={{ position: "relative", width: "500px" }}
                    ref={filterWrapperRef}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #c6d6f5",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        width: "100%",
                      }}
                    >
                      <img
                        src="/assets/images/search-icon.png"
                        style={{ width: "18px", marginLeft: "8px" }}
                      />
                      <input
                        type="text"
                        placeholder="Search slide name or title..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setIsFilterPanelOpen(true)}
                        style={{
                          border: "none",
                          outline: "none",
                          padding: "8px 8px",
                          width: "100%",
                          fontSize: "14px",
                          color: "#333",
                          backgroundColor: "transparent",
                        }}
                      />
                      {searchQuery && (
                        <X
                          size={24}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchQuery("");
                            setCurrentPage(1);
                            setIsFilterPanelOpen(false);
                          }}
                          style={{
                            position: "absolute",
                            right: "40px",
                            cursor: "pointer",
                            color: "#999",
                            padding: "4px",
                            zIndex: 10,
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#666")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#999")
                          }
                        />
                      )}
                      <ChevronDown
                        size={16}
                        style={{
                          marginRight: "8px",
                          color: "#999",
                          cursor: "pointer",
                          transform: isFilterPanelOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsFilterPanelOpen(!isFilterPanelOpen);
                        }}
                      />
                    </div>
                    {isFilterPanelOpen && (
                      <FilterPanel
                        filterValues={filterValues}
                        onFilterChange={(key, value) =>
                          setFilterValues((prev) => ({ ...prev, [key]: value }))
                        }
                        uniqueSlideTypes={uniqueSlideTypes}
                        uniqueViews={uniqueViews}
                        onApply={applyFilters}
                        onClear={clearFilters}
                      />
                    )}
                  </div>
                </div>
                <div className="col text-end">
                  {productPermissions.add && (
                    <button
                      className="btn me-8 mb-3"
                      style={{
                        height: "40px",
                        width: "140px",
                        color: "white",
                        backgroundColor: "#2696D5",
                      }}
                      onClick={() => navigate("/admin/content/contentAddNew")}
                    >
                      Add New
                    </button>
                  )}
                </div>
              </div>

              <div className="ps-22 pe-22 pb-22 rounded-5">
                <div className="row g-4 mt-24">
                  {currentItems.length > 0 ? (
                    currentItems.map((slide) => (
                      <div key={slide.id} className="col-12">
                        <div
                          className="card h-100 shadow-sm border-0 rounded-8"
                          style={{
                            backgroundColor: "#fff",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.transform =
                              "translateY(-2px)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.transform = "translateY(0)")
                          }
                        >
                          <div className="row g-0 h-100 align-items-center">
                            <div className="col-10">
                              <div className="row">
                                <div className="col-4 d-flex align-items-center justify-content-center px-22">
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "120px",
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                      backgroundColor: "#e9f0f4",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {slide.imageUrl ? (
                                      <img
                                        src={`${baseURL}/${slide.imageUrl}`}
                                        alt={slide.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <span
                                        className="text-muted"
                                        style={{ fontSize: "0.85rem" }}
                                      >
                                        No Image
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="col-3 p-3">
                                  <div className="mb-2">
                                    <strong style={{ fontSize: "0.85rem" }}>
                                      Slide Name:
                                    </strong>
                                    <br />
                                    <span
                                      style={{
                                        fontSize: "0.85rem",
                                        fontWeight: 500,
                                        color: "#4a5568",
                                      }}
                                    >
                                      {slide.name || "N/A"}
                                    </span>
                                  </div>
                                  <div className="mb-2">
                                    <strong style={{ fontSize: "0.85rem" }}>
                                      Slide Type:
                                    </strong>
                                    <br />
                                    <span
                                      style={{
                                        fontSize: "0.85rem",
                                        fontWeight: 500,
                                        color: "#4a5568",
                                      }}
                                    >
                                      {slide.type || "N/A"}
                                    </span>
                                  </div>
                                  <div>
                                    <strong style={{ fontSize: "0.85rem" }}>
                                      Slide Description:
                                    </strong>
                                    <br />
                                    <span
                                      style={{
                                        fontSize: "0.85rem",
                                        fontWeight: 500,
                                        color: "#4a5568",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {slide.description ||
                                        slide.title ||
                                        "N/A"}
                                    </span>
                                  </div>
                                </div>
                                <div className="col-3 p-3">
                                  <div className="mb-2">
                                    <strong style={{ fontSize: "0.85rem" }}>
                                      Mobile App or Website:
                                    </strong>
                                    <br />
                                    <span
                                      style={{
                                        fontSize: "0.85rem",
                                        fontWeight: 500,
                                        color: "#4a5568",
                                      }}
                                    >
                                      {getViewLabel(slide.view)}
                                    </span>
                                  </div>
                                  <div className="mb-2">
                                    <strong style={{ fontSize: "0.85rem" }}>
                                      Slide Title:
                                    </strong>
                                    <br />
                                    <span
                                      style={{
                                        fontSize: "0.85rem",
                                        fontWeight: 500,
                                        color: "#4a5568",
                                      }}
                                    >
                                      {slide.title || "N/A"}
                                    </span>
                                  </div>
                                  <div>
                                    <strong style={{ fontSize: "0.85rem" }}>
                                      Slide Link:
                                    </strong>
                                    <br />
                                    {slide.linkUrl ? (
                                      <a
                                        href={slide.linkUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-decoration-none text-primary"
                                        style={{
                                          fontSize: "0.85rem",
                                          fontWeight: 500,
                                          color: "#4a5568",
                                        }}
                                      >
                                        {slide.linkUrl.length > 30
                                          ? `${slide.linkUrl.substring(
                                              0,
                                              30,
                                            )}...`
                                          : slide.linkUrl}
                                      </a>
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: "0.85rem",
                                          fontWeight: 500,
                                          color: "#94a3b8",
                                        }}
                                      >
                                        N/A
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="col-2 d-flex ">
                                  <div
                                    style={{
                                      fontSize: "0.85rem",
                                      display: "flex",
                                      gap: "8px",
                                    }}
                                  >
                                    <span>Status</span>
                                    <div className="form-check form-switch">
                                      <input
                                        className={`form-check-input form-product-status-switch ${
                                          slide.status === "active"
                                            ? "switch-green"
                                            : "switch-orange"
                                        }`}
                                        type="checkbox"
                                        id={`statusSwitch-${slide.id}`}
                                        checked={slide.status === "active"}
                                        onChange={() =>
                                          handleStatusChange(
                                            slide.id,
                                            slide.status,
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              className="col-2 p-16 rounded-8"
                              style={{ backgroundColor: "#F7FAFC" }}
                            >
                              {productPermissions.view && (
                                <button
                                  onClick={() => handleView(slide.id)}
                                  title="Preview"
                                  style={{
                                    borderRadius: "4px",
                                    backgroundColor: "#D9EBFF",
                                    height: "39px",
                                    width: "100%",
                                    border: "none",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    padding: "0",
                                    margin: "0",
                                    fontSize: "14px",
                                    lineHeight: 1,
                                    outline: "none",
                                    marginTop: "16px",
                                    marginBottom: "16px",
                                    color: "#2B95D3",
                                  }}
                                >
                                  <img
                                    src="/assets/images/view-icon.png"
                                    alt="View"
                                    style={{
                                      width: "24px",
                                      height: "16px",
                                      marginRight: "5px",
                                    }}
                                  />
                                  Preview
                                </button>
                              )}
                              {productPermissions.edit && (
                                <button
                                  onClick={() => handleEdit(slide.id)}
                                  title="Edit"
                                  style={{
                                    borderRadius: "4px",
                                    backgroundColor: "#D9EBFF",
                                    height: "39px",
                                    width: "100%",
                                    border: "none",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    padding: "0",
                                    margin: "0",
                                    fontSize: "14px",
                                    lineHeight: 1,
                                    outline: "none",
                                    marginTop: "16px",
                                    marginBottom: "16px",
                                    color: "#2B95D3",
                                  }}
                                >
                                  <img
                                    src="/assets/images/edit-icon.png"
                                    alt="Edit"
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      objectFit: "contain",
                                      marginRight: "5px",
                                    }}
                                  />
                                  Edit
                                </button>
                              )}
                              {productPermissions.delete && (
                                <button
                                  onClick={() => handleDelete(slide.id)}
                                  title="Delete"
                                  style={{
                                    borderRadius: "4px",
                                    backgroundColor: "#D9EBFF",
                                    height: "39px",
                                    width: "100%",
                                    border: "none",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    padding: "0",
                                    margin: "0",
                                    fontSize: "14px",
                                    lineHeight: 1,
                                    outline: "none",
                                    marginTop: "16px",
                                    marginBottom: "16px",
                                    color: "#2B95D3",
                                  }}
                                >
                                  <img
                                    src="/assets/images/delete-icon.png"
                                    alt="Delete"
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      marginRight: "5px",
                                    }}
                                  />
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-12 text-center py-4">
                      No slides found.
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-16 p-16">
                  <div className="d-flex align-items-center">
                    <select
                      className="form-select d-inline-block w-auto"
                      value={itemsPerPage}
                      onChange={(e) => {
                        const val = e.target.value;
                        setItemsPerPage(val === "all" ? "all" : parseInt(val));
                        setCurrentPage(1);
                      }}
                      style={{
                        height: "34px",
                        fontSize: "14px",
                        minWidth: "70px",
                      }}
                    >
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="all">All</option>
                    </select>
                    <span className="ms-2">
                      Showing {showingFrom} to {showingTo} of{" "}
                      {filteredData.length} Slides
                    </span>
                  </div>
                  <nav>
                    <ul className="pagination mt-0">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          &laquo;
                        </button>
                      </li>
                      {[...Array(totalPage)].map((_, i) => (
                        <li
                          key={i + 1}
                          className={`page-item ${
                            currentPage === i + 1 ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))}
                      <li
                        className={`page-item ${
                          currentPage === totalPage ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPage}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </>
          )}
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </section>
      </div>
    </>
  );
};

export default ListSlide;

// --- Filter Panel Component ---
const FilterPanel = ({
  filterValues,
  onFilterChange,
  uniqueSlideTypes,
  uniqueViews,
  onApply,
  onClear,
}) => {
  return (
    <div
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        width: "500px",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        zIndex: 1000,
        padding: "16px",
        maxHeight: "400px",
        overflowY: "auto",
        marginTop: "10px",
      }}
    >
      <div className="row g-3">
        {/* View (Mobile App / Website) */}
        <div className="col-6 mb-2">
          <label className="form-label" style={{ fontSize: "13px" }}>
            Mobile App or Website
          </label>
          <select
            className="form-select"
            value={filterValues.view || ""}
            onChange={(e) => onFilterChange("view", e.target.value)}
          >
            <option value="">All</option>
            <option value="mobile">Mobile App</option>
            <option value="web">Website</option>
            {uniqueViews
              .filter((v) => !["mobile", "web"].includes(v))
              .map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
          </select>
        </div>
        {/* Slide Type */}
        <div className="col-6 mb-2">
          <label className="form-label" style={{ fontSize: "13px" }}>
            Slide Type
          </label>
          <select
            className="form-select"
            value={filterValues.slideType || ""}
            onChange={(e) => onFilterChange("slideType", e.target.value)}
          >
            <option value="">All Types</option>
            {uniqueSlideTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        {/* Status */}
        <div className="col-6 mb-2">
          <label className="form-label" style={{ fontSize: "13px" }}>
            Status
          </label>
          <select
            className="form-select"
            value={filterValues.status || ""}
            onChange={(e) => onFilterChange("status", e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="d-flex gap-2 mt-3">
        <button
          onClick={onApply}
          style={{
            color: "#fff",
            backgroundColor: "#2b95d3",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            padding: "8px 16px",
            minWidth: "120px",
            height: "40px",
            border: "none",
          }}
        >
          Apply
        </button>
        <button
          onClick={onClear}
          style={{
            color: "#2692d2",
            backgroundColor: "#d9ebff",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            padding: "8px 16px",
            minWidth: "120px",
            height: "40px",
            border: "none",
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
};
